import { useEffect, useState, memo } from "react";
import { useTranslation } from "react-i18next";
import { Bar } from "react-chartjs-2";
import { http } from "../helper";
import { BaseUrl } from "../common/config";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
);

export const CustomerReport = memo(() => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const [filter, setFilter] = useState("monthly");
  const getCurrentFiscalYear = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1;
    // Fiscal year starts in September
    const startYear = month >= 9 ? year : year - 1;
    const endYear = startYear + 1;
    return `${startYear}-${endYear}`;
  };
  const [period, setPeriod] = useState(getCurrentFiscalYear());
  const [viewType, setViewType] = useState("customer");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [chartData, setChartData] = useState({ labels: [], datasets: [] });
  const updateChartData = (labels, data, label) => {
    setChartData({
      labels: labels,
      datasets: [
        {
          label: label,
          data: data,
          backgroundColor: "#8dc5e4",
          barThickness: 30,
          borderRadius: 4,
          pointRadius: 0,
        },
      ],
    });
  };

  const noDataPlugin = {
    id: "noDataPlugin",
    beforeDraw: (chart) => {
      const { datasets } = chart.data;
      const hasData = datasets.some((dataset) => dataset.data.length > 0);
      if (!hasData) {
        const ctx = chart.ctx;
        const { width, height } = chart;
        ctx.save();
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.font = "16px Arial";
        ctx.fillStyle = "#eb9f9d";
        ctx.fillText(t("no_data_filtered_search_txt"), width / 2, height / 2);
        ctx.restore();
      }
    },
  };

  useEffect(() => {
    ChartJS.register(noDataPlugin);
  }, [t]);
  // Prepares chart data with fiscal year starting from September
  const prepareChartData = (rawData) => {
    const chartTitle = t("no_of_customers_ordered_txt");
    const fiscalMonthMap = {
      9: "09",
      10: "10",
      11: "11",
      12: "12",
      1: "01",
      2: "02",
      3: "03",
      4: "04",
      5: "05",
      6: "06",
      7: "07",
      8: "08",
    };
    const fiscalOrder = [
      "09",
      "10",
      "11",
      "12",
      "01",
      "02",
      "03",
      "04",
      "05",
      "06",
      "07",
      "08",
    ];

    const fiscalQuarterMap = {
      1: t("quarter_sep_nov"),
      2: t("quarter_dec_feb"),
      3: t("quarter_mar_may"),
      4: t("quarter_jun_aug"),
    };

    const fiscalQuarterMapMonth = {
      9: 1,
      10: 1,
      11: 1,
      12: 2,
      1: 2,
      2: 2,
      3: 3,
      4: 3,
      5: 3,
      6: 4,
      7: 4,
      8: 4,
    };

    const isCurrentFiscalYear = () => {
      const now = new Date();
      const startFY =
        now.getMonth() + 1 >= 9 ? now.getFullYear() : now.getFullYear() - 1;
      const endFY = startFY + 1;
      const currentPeriod = `${startFY}-${endFY}`;
      return period === currentPeriod;
    };

    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentQuarter = fiscalQuarterMapMonth[currentMonth];
    const fiscalStartYear = parseInt(period.split("-")[0]);
    const quarterlyAccounts = {};
    const monthlyAccounts = {};
    if (filter === "monthly") {
      rawData.forEach((item) => {
        const month = item._id?.month || item.month;
        const fiscalMonthName = fiscalMonthMap[month];
        if (!fiscalMonthName) return;
        const fiscalYearSuffix =
          month >= 9 ? fiscalStartYear : fiscalStartYear + 1;
        const fullLabel = `${fiscalMonthName}/${fiscalYearSuffix}`;
        if (!monthlyAccounts[fullLabel]) {
          monthlyAccounts[fullLabel] = new Set();
        }
        if (item.accountCode) {
          monthlyAccounts[fullLabel].add(item.accountCode);
        }
      });
      const fiscalLabels = Object.keys(monthlyAccounts).sort(
        (a, b) =>
          fiscalOrder.indexOf(a.split("/")[0]) -
          fiscalOrder.indexOf(b.split("/")[0]),
      );
      const labels = fiscalLabels.filter((label) => {
        if (!isCurrentFiscalYear()) return true;
        const currentFiscalMonthName = fiscalMonthMap[currentMonth];
        const currentFiscalYearSuffix =
          currentMonth >= 9 ? fiscalStartYear : fiscalStartYear + 1;
        const currentFullLabel = `${currentFiscalMonthName}/${currentFiscalYearSuffix}`;
        return (
          fiscalLabels.indexOf(label) <= fiscalLabels.indexOf(currentFullLabel)
        );
      });
      const counts = labels.map((label) => monthlyAccounts[label]?.size || 0);
      updateChartData(labels, counts, chartTitle);
    } else if (filter === "quarterly") {
      rawData.forEach((item) => {
        const quarter = item.quarter;
        if (!quarter) return;
        const fiscalQuarter = fiscalQuarterMap[quarter];
        if (!fiscalQuarter) return;
        if (!quarterlyAccounts[fiscalQuarter]) {
          quarterlyAccounts[fiscalQuarter] = new Set();
        }
        if (item.accountCode) {
          quarterlyAccounts[fiscalQuarter].add(item.accountCode);
        }
      });
      const labels = Object.keys(quarterlyAccounts);
      const counts = labels.map((q) => quarterlyAccounts[q]?.size || 0);
      updateChartData(labels, counts, chartTitle);
    } else if (filter === "FYdata") {
      const totalAccounts = new Set();
      rawData.forEach((item) => {
        if (item.accountCode) {
          totalAccounts.add(item.accountCode);
        }
      });
      const totalCount = totalAccounts.size;
      updateChartData([period], [totalCount], chartTitle);
    } else if (filter === "weekly") {
      const dateMap = {};
      rawData.forEach((item) => {
        const createdAt = new Date(item.createdAt);
        const dd = String(createdAt.getDate()).padStart(2, "0");
        const mm = String(createdAt.getMonth() + 1).padStart(2, "0");
        const yyyy = createdAt.getFullYear();
        const dateStr = `${dd}/${mm}/${yyyy}`;
        if (!dateMap[dateStr]) {
          dateMap[dateStr] = new Set();
        }
        if (item.accountCode) {
          dateMap[dateStr].add(item.accountCode);
        }
      });
      const sortedDates = Object.keys(dateMap).sort((a, b) => {
        const [ad, am, ay] = a.split("/").map(Number);
        const [bd, bm, by] = b.split("/").map(Number);
        return new Date(ay, am - 1, ad) - new Date(by, bm - 1, bd);
      });
      const counts = sortedDates.map((date) => dateMap[date].size);
      updateChartData(sortedDates, counts, chartTitle);
    } else if (filter === "custom") {
      const uniqueAccounts = new Set();
      rawData.forEach((item) => {
        if (item.accountCode) {
          uniqueAccounts.add(item.accountCode);
        }
      });
      const dd = (d) => String(d.getDate()).padStart(2, "0");
      const mm = (d) => String(d.getMonth() + 1).padStart(2, "0");
      const yyyy = (d) => d.getFullYear();
      const fromDateStr = `${dd(fromDate)}/${mm(fromDate)}/${yyyy(fromDate)}`;
      const toDateStr = `${dd(toDate)}/${mm(toDate)}/${yyyy(toDate)}`;
      const rangeLabel = `${fromDateStr} - ${toDateStr}`;
      updateChartData([rangeLabel], [uniqueAccounts.size], chartTitle);
    }
  };
  //for oreders api
  useEffect(() => {
    if (viewType !== "order") return;
    const URL = BaseUrl + "admin/topcustomers";
    let payload = {};
    if (filter === "custom" && (!fromDate || !toDate)) return;
    if (filter === "custom") {
      payload = {
        filter,
        datefrom: format(new Date(fromDate), "yyyy-MM-dd"),
        dateto: format(new Date(toDate), "yyyy-MM-dd"),
      };
      setShowDatePicker(false);
    } else if (["weekly", "monthly", "quarterly", "FYdata"].includes(filter)) {
      payload = {
        filter,
        datefrom: "",
        dateto: "",
        period,
      };
    } else {
      payload = {
        filter: parseInt(filter),
        datefrom: "",
        dateto: "",
      };
    }
    setIsLoading(true);
    http
      .post(URL, payload)
      .then((res) => {
        setIsLoading(false);
        prepareChartData(res.data?.data || []);
      })
      .catch((err) => {
        console.error("API error:", err);
        setIsLoading(false);
      });
  }, [filter, fromDate, toDate, period, viewType]);
  //for customer api
  useEffect(() => {
    if (viewType !== "customer") return;
    const URL = BaseUrl + "admin/getordercountaccountdetails";
    const chartTitle = t("no_of_customers_onboarded_txt");
    let payload = {};
    if (filter === "custom" && (!fromDate || !toDate)) return;
    if (filter === "custom") {
      payload = {
        filter,
        datefrom: format(new Date(fromDate), "yyyy-MM-dd"),
        dateto: format(new Date(toDate), "yyyy-MM-dd"),
        period,
      };
    } else if (["weekly", "monthly", "quarterly", "FYdata"].includes(filter)) {
      payload = {
        filter,
        datefrom: "",
        dateto: "",
        period,
      };
    } else {
      payload = {
        filter: parseInt(filter),
        datefrom: "",
        dateto: "",
        period,
      };
    }
    setIsLoading(true);
    http
      .post(URL, payload)
      .then((res) => {
        setIsLoading(false);
        const rawData = res.data?.data || [];
        let labels = [];
        let data = [];
        switch (filter) {
          case "monthly":
            labels = rawData.map((item) => {
              const m = String(item._id.month).padStart(2, "0");
              const y = item._id.year;
              return `${m}/${y}`;
            });
            data = rawData.map((item) => item.accountCount || 0);
            updateChartData(labels, data, chartTitle);
            break;
          case "quarterly":
            const quarterMap = {
              1: t("quarter_sep_nov"),
              2: t("quarter_dec_feb"),
              3: t("quarter_mar_may"),
              4: t("quarter_jun_aug"),
            };
            labels = rawData.map(
              (item) => `${quarterMap[item._id.quarter] || t("unknown_txt")} `,
            );
            data = rawData.map((item) => item.accountCount || 0);
            updateChartData(labels, data, chartTitle);
            break;
          case "FYdata":
            labels = [period];
            data = rawData.map((item) => item.accountCount || 0);
            updateChartData(labels, data, chartTitle);
            break;
          case "custom":
            if (rawData.length === 0) {
              updateChartData([], [], chartTitle);
            } else {
              const labelRange = `${format(new Date(fromDate), "dd/MM/yyyy")} - ${format(new Date(toDate), "dd/MM/yyyy")}`;
              labels = [labelRange];
              const totalCount = rawData.reduce(
                (sum, item) => sum + (item.accountCount || 0),
                0,
              );
              data = [totalCount];
              updateChartData(labels, data, chartTitle);
            }
            break;
          case "weekly":
            labels = rawData.map((item) =>
              format(new Date(item._id.date), "dd/MM/yyyy"),
            );
            data = rawData.map((item) => item.accountCount || 0);
            updateChartData(labels, data, chartTitle);
            break;
          default:
            console.warn("Unhandled filter type:", filter);
            break;
        }
      })
      .catch((err) => {
        console.error("API error:", err);
        setIsLoading(false);
      });
  }, [filter, fromDate, toDate, period, viewType]);

  const changeFilterHandler = (e) => {
    const value = e.target.value;
    if (value === "custom") {
      setShowDatePicker(true);
    } else {
      setShowDatePicker(false);
      setShowDatePicker(false);
      setFromDate(null);
      setToDate(null);
    }
    setFilter(value);
  };

  const DateChange = (date, type) => {
    if (type === "from") {
      setFromDate(date);
      if (toDate && date && date > toDate) {
        setToDate(null);
      }
    } else {
      if (!fromDate || date >= fromDate) {
        setToDate(date);
        if (fromDate && date) {
          setShowDatePicker(false);
        }
      } else {
        setToDate(null);
      }
    }
  };

  const generateFiscalYears = () => {
    const years = [];
    const today = new Date();
    const currentYear = today.getFullYear();
    const month = today.getMonth() + 1;
    const startYear = month >= 9 ? currentYear : currentYear - 1;
    for (let y = startYear; y >= 2023; y--) {
      years.push(`${y}-${y + 1}`);
    }
    return years;
  };

  const shouldShowPeriodDropdown = [
    "monthly",
    "quarterly",
    "yearly",
    "FYdata",
  ].includes(filter);

  const loadingBuffer = isLoading ? "loading-buffer" : "";

  return (
    <div
      className={`chart-container chart-week-revenue ${loadingBuffer}`}
      style={{ minHeight: "600px" }}
    >
      <div className="chart-Hdr">
        <div className="chart-fltr chart-full-flex">
          <div className="chart-fltr-left">
            <select
              value={viewType}
              onChange={(e) => setViewType(e.target.value)}
            >
              <option value="customer">
                {t("no_of_customers_onboarded_txt")}
              </option>
              <option value="order">{t("no_of_customers_ordered_txt")}</option>
            </select>
          </div>
          <div
            className="chart-fltr"
            onMouseEnter={() => {
              if (filter === "custom" && !showDatePicker) {
                setShowDatePicker(true);
              }
            }}
            onMouseLeave={() => {
              if (filter === "custom" && showDatePicker) {
                setShowDatePicker(false);
              }
            }}
          >
            <div className="chart-fltr">
              <div className="chart-fltr-right">
                <select
                  value={filter}
                  onChange={changeFilterHandler}
                  className="filter-dropdown"
                >
                  <option value="weekly">{t("current_week_txt")}</option>
                  <option value="monthly">{t("monthly_txt")}</option>
                  <option value="quarterly">{t("quarterly_txt")}</option>
                  <option value="FYdata">{t("fy_data_txt")}</option>
                  <option value="custom">{t("custom_date_txt")}</option>
                </select>
                {filter === "custom" && showDatePicker && (
                  <div className="date-picker-container">
                    <label>{t("from_txt")}:</label>
                    <DatePicker
                      selected={fromDate}
                      onChange={(date) => DateChange(date, "from")}
                      dateFormat="dd/MM/yyyy"
                      maxDate={new Date()}
                      placeholderText={t("select_date_txt")}
                    />
                    <label>{t("to_txt")}:</label>
                    <DatePicker
                      selected={toDate}
                      onChange={(date) => DateChange(date, "to")}
                      dateFormat="dd/MM/yyyy"
                      maxDate={new Date()}
                      placeholderText={t("select_date_txt")}
                    />
                  </div>
                )}
              </div>
              {shouldShowPeriodDropdown && (
                <select
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                >
                  {generateFiscalYears().map((fy) => (
                    <option key={fy} value={fy}>
                      {fy}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
        </div>
      </div>
      <Bar
        data={chartData}
        options={{
          responsive: true,
          plugins: {
            legend: { display: true, onClick: () => {} },
            tooltip: {
              callbacks: {
                title: (items) => `${items[0].label}`,
                label: (item) => {
                  return `${viewType === "customer" ? t("no_of_customers_onboarded_txt") : t("no_of_customers_ordered_txt")}: ${item.formattedValue}`;
                },
              },
            },
            datalabels: {
              display: false,
            },
            noDataPlugin,
          },
          scales: {
            x: {
              grid: { display: false },
              ticks: { align: "center" },
              offset: true,
            },
            y: {
              beginAtZero: true,
            },
          },
        }}
      />
    </div>
  );
});
